import { env } from "~/env";

/**
 * `headers` are the request headers the client MUST send on the PUT for the
 * signature to hold. For public assets on DO Spaces that includes
 * `x-amz-acl: public-read` — without it the object uploads but stays private —
 * so pass them straight to `uploadToS3`.
 */
export async function presignUpload(
  filename: string,
  type: string,
  accessToken: string,
): Promise<{ url: string; key: string; headers?: Record<string, string>; public_url?: string }> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/uploads/presign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ filename, type }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.message || "Failed to presign upload");
  }
  return res.json();
}

/**
 * Presign a PRIVATE document upload — qualification, insurance, first-aid and
 * safety-training certificates (#513).
 *
 * Unlike `presignUpload`, the returned key is an opaque `documents/<uuid>` with
 * no filename in it, and there is deliberately no public URL to construct: the
 * caller persists the KEY, and the API hands back a short-lived signed URL when
 * an authorised viewer asks to read it.
 */
export async function presignDocumentUpload(
  filename: string,
  type: string,
  accessToken: string,
): Promise<{ url: string; key: string; filename: string }> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/uploads/presign-document`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ filename, type }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.message || "Failed to presign document upload");
  }
  return res.json();
}

/**
 * Presign a PRIVATE support-ticket screenshot upload. The key is an opaque
 * `bug_report_uploads/<userId>/<uuid>`; there is no public URL to construct and the
 * object is never made publicly readable. Persist the key only.
 */
export async function presignBugReportUpload(
  filename: string,
  type: string,
  accessToken: string,
): Promise<{ url: string; key: string; filename: string; headers?: Record<string, string> }> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/uploads/presign-bug-report`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ filename, type }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.message || "Failed to presign upload");
  }
  return res.json();
}

/**
 * The public URL of an object uploaded through `presignUpload`. The presigned
 * PUT URL is the object's own URL plus the signature query string, so dropping
 * the query gives the address on whichever bucket the API actually signed for —
 * no bucket name is baked into the web app.
 */
export function publicUrlFromPresigned(presignedUrl: string): string {
  const u = new URL(presignedUrl);
  return `${u.origin}${u.pathname}`;
}

/**
 * The public URL to STORE for an object uploaded through `presignUpload`.
 *
 * Since #1240 the API mints it: `POST /uploads/presign` returns `public_url`
 * from `storage.publicUrl(key)` — the same call `MemberAvatarService` uses for
 * member avatars — so every public upload is addressed by one server-side
 * convention and a bucket host can no longer drift out of the `next/image`
 * allowlist unnoticed (the QA S3 host did exactly that; see `image-hosts.js`).
 * Pass the presign response's `public_url` as the third argument.
 *
 * The two client-side fallbacks remain for an API that has not deployed the
 * field yet (api and web deploy independently):
 *
 *   1. `NEXT_PUBLIC_UPLOADS_BASE_URL` — the public origin of the uploads bucket
 *      (e.g. `https://pathcollectiveqa.syd1.digitaloceanspaces.com`, or a CDN
 *      in front of it). It is deliberately separate from
 *      `NEXT_PUBLIC_IMAGE_BASE_URL`, which resolves relative keys already in
 *      the database and points at Supabase storage. It still matters after
 *      #1240 because `next.config.js` reads it at build time to allowlist the
 *      host for `next/image`.
 *   2. The address on the bucket the API actually signed for
 *      (`publicUrlFromPresigned`), so a stack with neither still stores a
 *      working URL — rendered only if that host is allowlisted.
 */
export function uploadedObjectUrl(key: string, presignedUrl: string, publicUrl?: string | null): string {
  const fromApi = publicUrl?.trim();
  if (fromApi) return fromApi;
  const base = env.NEXT_PUBLIC_UPLOADS_BASE_URL?.trim().replace(/\/+$/, "");
  if (!base) return publicUrlFromPresigned(presignedUrl);
  return `${base}/${key.replace(/^\/+/, "")}`;
}

export async function uploadToS3(
  url: string,
  file: File,
  headers?: Record<string, string>,
): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
      ...headers,
    },
    body: file,
  });
  if (!res.ok) {
    // #1179 FR-7 — no storage vendor in a message a user reads. Documents
    // (facilitator sign-up) come through here too, hence the noun.
    const noun = file.type.startsWith("image/") ? "image" : "file";
    throw new Error(`Couldn't upload that ${noun} — please try again.`);
  }
}


